import React, { useState, useRef, useEffect } from 'react';
import FeatherIcon from 'feather-icons-react';

interface MultiSelectDropdownProps {
  label: string;
  options: string[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const handleRemoveToken = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedValues.filter(v => v !== value));
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', marginBottom: '12px' }}>
      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, fontSize: '0.9em' }}>
        {label} {selectedValues.length > 0 && `(${selectedValues.length})`}
      </label>
      
      {/* Tokens container - clickable to open dropdown */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '4px',
          minHeight: '32px',
          padding: selectedValues.length === 0 ? '6px 8px' : '4px 30px 4px 4px',
          borderRadius: '4px',
          backgroundColor: disabled ? '#f5f5f5' : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          alignItems: 'center',
          position: 'relative'
        }}
      >
        {selectedValues.length === 0 ? (
          <span style={{ color: '#999', fontSize: '0.9em', flex: 1 }}>
            Select {label}...
          </span>
        ) : (
          selectedValues.map((value) => (
            <span
              key={value}
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 8px',
                backgroundColor: '#e8f4f8',
                border: '1px solid #4a90e2',
                borderRadius: '12px',
                fontSize: '0.85em',
                color: '#333'
              }}
            >
              <span>{value.replace(/_/g, ' ')}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveToken(value, e);
                  }}
                  disabled={disabled}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#4a90e2',
                    fontSize: '14px',
                    lineHeight: 1,
                    opacity: 1,
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!disabled) {
                      e.currentTarget.style.opacity = '0.6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!disabled) {
                      e.currentTarget.style.opacity = '1';
                    }
                  }}
                  title="Remove"
                >
                  <FeatherIcon icon="x" size={12} />
                </button>
            </span>
          ))
        )}
        {/* Chevron icon - always visible on the right */}
        <span
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            color: '#999',
            pointerEvents: 'none'
          }}
        >
          <FeatherIcon icon={isOpen ? "chevron-up" : "chevron-down"} size={16} />
        </span>
      </div>

      {isOpen && !disabled && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            maxHeight: '250px',
            overflowY: 'auto',
            zIndex: 1000,
            marginTop: '2px',
          }}
        >
          {/* Select All option */}
          {options.length > 0 && (
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #e0e0e0',
                fontWeight: 600,
                fontSize: '0.85em',
              }}
              onClick={handleSelectAll}
            >
              <input
                type="checkbox"
                checked={selectedValues.length === options.length}
                onChange={handleSelectAll}
                style={{ marginRight: '8px', cursor: 'pointer' }}
              />
              {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
            </div>
          )}

          {/* Options */}
          {options.map((option) => (
            <div
              key={option}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: selectedValues.includes(option) ? '#e8f4f8' : '#fff',
                borderBottom: '1px solid #f0f0f0',
                fontSize: '0.9em',
              }}
              onClick={() => handleToggle(option)}
              onMouseEnter={(e) => {
                if (!selectedValues.includes(option)) {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (!selectedValues.includes(option)) {
                  e.currentTarget.style.backgroundColor = '#fff';
                }
              }}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => handleToggle(option)}
                style={{ marginRight: '8px', cursor: 'pointer' }}
              />
              <span>{option.replace(/_/g, ' ')}</span>
            </div>
          ))}

          {options.length === 0 && (
            <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '0.9em' }}>
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

